import { useState } from "react"

import { useMutation } from "@apollo/client"

import { useErrorNotification } from "@/souhl_admin/components/ui/ErrorNotification/ErrorNotification"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

const HUBSPOT_DEAL_ID_PATTERN = /^[0-9]+$/

const UpdateCompanyHubspotContractIDs = graphql(/* GraphQL */ `
	mutation UpdateCompanyHubspotContractIDsForGeneralInfo(
		$input: UpdateCompanyHubspotContractIDsInput!
	) {
		updateCompanyHubspotContractIDs(input: $input) {
			hubspotContracts {
				...HubspotContractsSection_HubspotContract
			}
		}
	}
`)

// inlineError は入力起因 (重複 / 数値以外) を入力欄直下に表示するために使い、
// addError は通信起因 (mutation 失敗) をトースト通知するために使う。
export const useHubspotContractsEdit = (
	companyId: string,
	currentContractIds: readonly string[]
) => {
	const { addError } = useErrorNotification()
	const [editing, setEditing] = useState(false)
	const [draftIds, setDraftIds] = useState<string[]>([])
	const [newId, setNewId] = useState("")
	const [inlineError, setInlineError] = useState<string | null>(null)

	const [updateMutation, { loading: saving }] = useMutation(
		UpdateCompanyHubspotContractIDs,
		{
			refetchQueries: ["GetCompanyForGeneralInfo"],
			awaitRefetchQueries: true,
		}
	)

	const startEdit = () => {
		setDraftIds([...currentContractIds])
		setNewId("")
		setInlineError(null)
		setEditing(true)
	}

	const cancelEdit = () => {
		setEditing(false)
		setDraftIds([])
		setNewId("")
		setInlineError(null)
	}

	const addId = () => {
		const trimmed = newId.trim()
		if (!trimmed) return
		if (!HUBSPOT_DEAL_ID_PATTERN.test(trimmed)) {
			setInlineError("HubSpot Deal ID は数値で入力してください")
			return
		}
		if (draftIds.includes(trimmed)) {
			setInlineError(`ID "${trimmed}" は既に追加されています`)
			return
		}
		setDraftIds([...draftIds, trimmed])
		setNewId("")
		setInlineError(null)
	}

	const removeId = (id: string) => {
		setDraftIds(draftIds.filter((x) => x !== id))
	}

	// 全置換 mutation のため、削除がある場合のみ確認ダイアログを出す。
	// 追加だけならソフトデリート発生せず安全なのでサイレント保存する。
	const save = () => {
		const currentSet = new Set(currentContractIds)
		const draftSet = new Set(draftIds)
		const added = draftIds.filter((id) => !currentSet.has(id))
		const removed = currentContractIds.filter((id) => !draftSet.has(id))

		if (added.length === 0 && removed.length === 0) {
			cancelEdit()
			return
		}

		if (removed.length > 0) {
			const lines = [
				"以下の HubSpot 取引の紐付けを解除します。",
				`削除: ${removed.join(", ")}`,
			]
			if (added.length > 0) lines.push(`追加: ${added.join(", ")}`)
			lines.push("よろしいですか？")
			if (!window.confirm(lines.join("\n"))) return
		}

		setInlineError(null)
		updateMutation({
			variables: {
				input: {
					companyId,
					hubspotContractIds: draftIds,
				},
			},
		})
			.then(() => {
				setEditing(false)
				setDraftIds([])
				setNewId("")
			})
			.catch((err: Error) => {
				addError(`HubSpot 取引の更新に失敗しました: ${err.message}`)
			})
	}

	return {
		editing,
		draftIds,
		newId,
		inlineError,
		saving,
		setNewId,
		startEdit,
		cancelEdit,
		addId,
		removeId,
		save,
	}
}
